// Shared OS metadata helpers for the multi-OS capture / migrate flows.
// Source of truth is `MigratePC.jsx` — extracted here so the redesigned
// PC Capture & Apply page (`Migrations.jsx`) renders the same badges,
// labels, and agent OS detection without duplicating the logic.

/**
 * Robustly map a raw OS string (from the agent record's `os` field, or
 * an OS-release string) into one of the canonical buckets we render UI
 * for. Returns null when the platform can't be classified.
 *
 * Order matters — Darwin kernel reports include the substring "win"
 * (e.g. "Darwin … win"), so macOS is checked before Windows.
 */
export function normalizeOs(raw) {
    if (!raw) return null;
    const s = String(raw).toLowerCase();
    if (s.includes("mac") || s.includes("darwin") || s.includes("os x") || s.includes("osx")) return "macos";
    if (s.includes("win")) return "windows";
    if (/linux|ubuntu|debian|rhel|centos|fedora|alma|rocky|arch|suse|amazon|unix/.test(s)) return "linux";
    return null;
}

/**
 * Theme tokens per OS. `dot` is the small status-pill colour used in
 * dropdowns; `short` is the 3-letter badge used in compact tables.
 */
export const OS_META = {
    windows: { label: "Windows",  color: "#00A4EF", dot: "#00A4EF", short: "WIN" },
    macos:   { label: "macOS",    color: "#a3a3a3", dot: "#9CA3AF", short: "MAC" },
    linux:   { label: "Linux",    color: "#F97316", dot: "#F97316", short: "LNX" },
};

/** Get display metadata for an OS key (or a sane fallback for unknown). */
export function osBadge(os) {
    return OS_META[os] || { label: os || "Unknown", color: "#6b7280", dot: "#6b7280", short: "?" };
}

/**
 * Default user-profile folder presets per platform. Each entry has
 *   `rel`        — path relative to the user's home (`C:\Users\<u>\…`,
 *                  `/Users/<u>/…`, `/home/<u>/…`)
 *   `label`      — operator-facing label
 *   `defaultOn`  — pre-checked in the New Capture dialog
 * The rel path string ALSO serves as the artifact-name suffix the agent
 * uses when uploading the captured archive — keep them stable.
 */
export const PROFILE_FOLDERS_BY_OS = {
    windows: [
        { rel: "Desktop",          label: "Desktop",                            defaultOn: true  },
        { rel: "Documents",        label: "Documents",                          defaultOn: true  },
        { rel: "Downloads",        label: "Downloads",                          defaultOn: false },
        { rel: "Pictures",         label: "Pictures",                           defaultOn: false },
        { rel: "Music",            label: "Music",                              defaultOn: false },
        { rel: "Videos",           label: "Videos",                             defaultOn: false },
        { rel: "Favorites",        label: "Favorites (browser bookmarks)",      defaultOn: true  },
        { rel: "Links",            label: "Links",                              defaultOn: false },
        { rel: "Contacts",         label: "Contacts",                           defaultOn: false },
        { rel: "Saved Games",      label: "Saved Games",                        defaultOn: false },
        { rel: "AppData\\Roaming", label: "AppData\\Roaming",                   defaultOn: true  },
        { rel: "AppData\\Local",   label: "AppData\\Local (heavy — caches)",    defaultOn: false },
    ],
    macos: [
        { rel: "Desktop",                      label: "Desktop",                        defaultOn: true  },
        { rel: "Documents",                    label: "Documents",                      defaultOn: true  },
        { rel: "Downloads",                    label: "Downloads",                      defaultOn: false },
        { rel: "Pictures",                     label: "Pictures",                       defaultOn: false },
        { rel: "Music",                        label: "Music",                          defaultOn: false },
        { rel: "Movies",                       label: "Movies",                         defaultOn: false },
        { rel: "Library/Preferences",          label: "Library/Preferences (.plist)",   defaultOn: true  },
        { rel: "Library/Application Support",  label: "Library/Application Support",    defaultOn: true  },
        { rel: "Library/Keychains",            label: "Library/Keychains",              defaultOn: false },
        { rel: "Library/Containers",           label: "Library/Containers (sandboxed apps — heavy)", defaultOn: false },
        { rel: ".ssh",                         label: ".ssh (SSH keys + config)",        defaultOn: true  },
        { rel: ".config",                      label: ".config",                        defaultOn: false },
    ],
    linux: [
        { rel: "Desktop",      label: "Desktop",                                  defaultOn: true  },
        { rel: "Documents",    label: "Documents",                                defaultOn: true  },
        { rel: "Downloads",    label: "Downloads",                                defaultOn: false },
        { rel: "Pictures",     label: "Pictures",                                 defaultOn: false },
        { rel: "Music",        label: "Music",                                    defaultOn: false },
        { rel: "Videos",       label: "Videos",                                   defaultOn: false },
        { rel: ".config",      label: ".config (XDG_CONFIG_HOME)",                defaultOn: true  },
        { rel: ".local/share", label: ".local/share (apps state)",                defaultOn: true  },
        { rel: ".ssh",         label: ".ssh (SSH keys + config)",                 defaultOn: true  },
        { rel: ".gnupg",       label: ".gnupg",                                   defaultOn: false },
        { rel: ".bashrc",      label: ".bashrc / shell rc files",                 defaultOn: true  },
        { rel: ".cache",       label: ".cache (heavy — usually skip)",            defaultOn: false },
    ],
};

/**
 * Capture phases per OS. Each entry maps to a backend phase key the
 * `start_capture` endpoint accepts (today only the Windows set has
 * real agent handlers — Linux/macOS phases enqueue commands that the
 * cross-platform agent will fulfil in the upcoming v1.1.x release).
 */
export const CAPTURE_PHASES_BY_OS = {
    windows: [
        { key: "include_apps",     label: "Apps",            desc: "Installed software via HKLM Uninstall + winget IDs", defaultOn: true },
        { key: "include_registry", label: "Registry",        desc: "HKCU + HKLM\\SOFTWARE + WOW6432 hives via reg.exe",   defaultOn: true },
        { key: "include_settings", label: "Settings",        desc: "Power scheme, Wi-Fi profiles, printers, network",     defaultOn: true },
    ],
    macos: [
        { key: "include_apps",     label: "Applications",    desc: "Homebrew leaves + /Applications/*.app + Mac App Store IDs", defaultOn: true },
        { key: "include_settings", label: "Preferences",     desc: "~/Library/Preferences plists + system defaults",            defaultOn: true },
    ],
    linux: [
        { key: "include_apps",     label: "Packages",        desc: "apt/dpkg manual selections + flatpak + snap (auto-detected)", defaultOn: true },
        { key: "include_settings", label: "Config",          desc: "/etc/* operator-edited files + dconf user database",          defaultOn: true },
    ],
};
